import { useCallback, useMemo, useState } from "react";
import { PageHeader, Workbench } from "@/shared/components";
import { ButtonLink, Choice, Icon, Panel, SelectField } from "@/ui";
import { PROVIDERS, usePreferenceStore } from "@/shared/stores/preferences";
import { useLatestRunningSession } from "@/shared/stores/sessionHistory";
import { useCredits } from "@/features/credits";
import type { PaymentRoute } from "@/shared/types";
import { useModules, useScope, useScopeRelated } from "./hooks/useSkills";
import { useStartSession } from "./hooks/useStartSession";
import { ScopePicker } from "./components/ScopePicker";
import { TouchedModules } from "./components/TouchedModules";
import { SessionPreview } from "./components/SessionPreview";
import { EvidenceRules } from "./components/EvidenceRules";
import { PaymentRoutePicker } from "./components/PaymentRoutePicker";
import { duration as fmtDuration } from "@/shared/utils/format";

const DURATIONS = [
  { value: 1500, title: "25 minutes", sub: "A short sitting · soft deadline" },
  { value: 3000, title: "50 minutes", sub: "A full sitting · soft deadline" },
  { value: 0, title: "Until I stop", sub: "Ends after the question in progress" },
] as const;

/* An open-ended Session still needs a positive duration on the wire; the
   deadline is soft either way. Twelve hours is "until I stop" expressed in
   the only unit the contract accepts. */
const OPEN_ENDED_SECONDS = 12 * 60 * 60;

export function SessionSetupScreen() {
  const { data: modules, isPending } = useModules();
  const prefs = usePreferenceStore((s) => s.prefs);
  const savePrefs = usePreferenceStore((s) => s.save);
  const running = useLatestRunningSession();
  const start = useStartSession();

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [durationChoice, setDurationChoice] = useState<number>(prefs.defaultDuration);
  /* Null until the Candidate touches the picker, and null is what goes on the
     wire — the server then settles it from the Key Vault. Choosing is what
     turns it into a route this screen is asking for. */
  const [routeChoice, setRouteChoice] = useState<PaymentRoute | null>(null);

  const { data: credits } = useCredits();
  const hasKey = Boolean(credits?.byok);
  /* What the server would settle on if nobody chose. Shown as the selection so
     the picker never reads as undecided. */
  const route: PaymentRoute = routeChoice ?? (hasKey ? "byok" : "credits");

  const moduleIds = useMemo(() => [...selected].sort(), [selected]);
  const { data: scope, isFetching: loadingScope } = useScope(moduleIds);
  const { data: touched } = useScopeRelated(moduleIds);

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const setMany = useCallback((ids: string[]) => setSelected(new Set(ids)), []);

  const durationSeconds = durationChoice === 0 ? OPEN_ENDED_SECONDS : durationChoice;

  /* Offered only once a scope exists to have suggested it, and only when it is
     not already one of the fixed options — two identical choices is a worse
     picker than three. */
  const suggestedSeconds = scope?.suggested_seconds ?? 0;
  const suggested =
    suggestedSeconds > 0 && !DURATIONS.some((d) => d.value === suggestedSeconds)
      ? suggestedSeconds
      : 0;

  const blocked =
    moduleIds.length === 0
      ? "Choose at least one Module before starting."
      : scope && scope.topic_count === 0
        ? "Nothing in this scope holds an examinable Topic."
        : null;

  return (
    <>
      <PageHeader title="New Session" sub="Scope, duration and who pays — fixed before the first question">
        {running ? (
          <ButtonLink to={`/examination/${running.id}`} variant="ghost" size="sm">
            <Icon name="resume" size={14} />
            Resume the open Session
          </ButtonLink>
        ) : null}
      </PageHeader>

      <Workbench
        narrow
        side={
          <SessionPreview
            scope={scope}
            loadingScope={loadingScope}
            moduleCount={moduleIds.length}
            durationSeconds={durationChoice === 0 ? 0 : durationSeconds}
            provider={prefs.provider}
            /* Nothing is in flight here any more: Begin is a navigation, and
               the Session starts on the screen it navigates to (ISSUE-0053).
               A spinner on this button would be a spinner for a route change. */
            starting={false}
            blocked={blocked}
            onBegin={() =>
              start.begin({
                moduleIds,
                durationSeconds,
                provider: prefs.provider,
                paymentRoute: routeChoice ?? undefined,
              })
            }
          />
        }
      >
        <p className="eyebrow">Before a single question</p>
        <h1 className="display-3 mt-4">Scope, duration, and who pays.</h1>
        <p className="prose mt-6">
          Three decisions, made once. After this the Session runs the plan it made and does not ask you
          anything else about itself.
        </p>

        <section className="mt-11" aria-labelledby="step-scope">
          <div className="section-head">
            <div>
              <span className="step-n">Step 01</span>
              <h2 className="h2 mt-3" id="step-scope">Scope</h2>
            </div>
            <span className="caption">
              {moduleIds.length === 0
                ? "Nothing chosen yet"
                : `${moduleIds.length} chosen · ${scope?.topic_count ?? "…"} Topics`}
            </span>
          </div>
          <ScopePicker
            modules={modules}
            loading={isPending}
            selected={selected}
            onToggle={toggle}
            onSetMany={setMany}
          />
          <TouchedModules touched={touched} onAdd={toggle} />
          <p className="caption mt-4">
            The scheduler picks Topics inside this scope. It can tell an unasked Topic from a failed one, which
            is the whole reason it can choose well.
          </p>
        </section>

        <section className="mt-11" aria-labelledby="step-duration">
          <div className="section-head">
            <div>
              <span className="step-n">Step 02</span>
              <h2 className="h2 mt-3" id="step-duration">Duration</h2>
            </div>
            <span className="caption">Soft deadline</span>
          </div>
          <div className="grid-3" role="radiogroup" aria-labelledby="step-duration">
            {/* The scope's own suggestion, offered as a choice rather than
                imposed as a default. It is the server's figure — the surface
                derives no time of its own, because doing that would be a
                second implementation of the pacing rule (ADR-0009). */}
            {suggested ? (
              <Choice
                name="duration"
                value={String(suggested)}
                checked={durationChoice === suggested}
                onChange={(v) => {
                  const next = Number(v);
                  setDurationChoice(next);
                  savePrefs({ ...prefs, defaultDuration: next });
                }}
                title={fmtDuration(suggested)}
                sub="Suggested · one question per Topic"
              />
            ) : null}
            {DURATIONS.map((d) => (
              <Choice
                key={d.value}
                name="duration"
                value={String(d.value)}
                checked={durationChoice === d.value}
                onChange={(v) => {
                  const next = Number(v);
                  setDurationChoice(next);
                  savePrefs({ ...prefs, defaultDuration: next });
                }}
                title={d.title}
                sub={d.sub}
              />
            ))}
          </div>
          <Panel tone="2" pad={6} className="mt-6 rule-note">
            <Icon name="timer" size={16} />
            <p className="body-sm dim" style={{ margin: 0 }}>
              The deadline is soft. When it passes the Session finishes the current Visit and then stops —
              never mid-answer. A half-examined answer would corrupt the record the Session exists to build.
            </p>
          </Panel>
        </section>

        <section className="mt-11" aria-labelledby="step-pays">
          <div className="section-head">
            <div>
              <span className="step-n">Step 03</span>
              <h2 className="h2 mt-3" id="step-pays">Who pays</h2>
            </div>
            <span className="caption">Fixed for the whole Session</span>
          </div>
          <PaymentRoutePicker
            route={route}
            hasKey={hasKey}
            fingerprint={credits?.byok?.fingerprint}
            onChange={setRouteChoice}
          />

          <div className="grid-2 mt-8">
            <SelectField
              label="Provider"
              options={PROVIDERS}
              value={prefs.provider}
              onChange={(e) => savePrefs({ ...prefs, provider: e.target.value })}
              hint="Recorded on every Evidence row alongside its cost. Kept for your next Session too."
            />
            <p className="caption" style={{ alignSelf: "end" }}>
              A Session that runs out of Credits is parked, not ended — topping up resumes it on the Topics it
              had not reached.
            </p>
          </div>
        </section>

        <section className="mt-11" aria-labelledby="step-rules">
          <div className="section-head">
            <div>
              <span className="step-n">Step 04</span>
              <h2 className="h2 mt-3" id="step-rules">Rules of evidence</h2>
            </div>
            <span className="caption">In force, not adjustable</span>
          </div>
          <EvidenceRules />
        </section>
      </Workbench>
    </>
  );
}
